import { AppLayout } from "@/components/layout/AppLayout";
import { useFleetRisks, FleetRisk, FleetRiskLevel } from "@/hooks/useFleetRisks";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { useState } from "react";

const severityStyles: Record<string, string> = {
  high: "border-l-red-500",
  medium: "border-l-amber-500",
  low: "border-l-emerald-500",
};

const severityBadge: Record<string, string> = {
  high: "bg-red-500/10 text-red-600 border-red-500/20",
  medium: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  low: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

const horizonLabels: Record<string, string> = {
  short: "Short-term",
  medium: "Medium-term",
  long: "Long-term",
};

const directionIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  up: TrendingUp,
  down: TrendingDown,
  neutral: Minus,
};

const riskLevelConfig: Record<FleetRiskLevel, { icon: React.ComponentType<{ className?: string }>; color: string; bg: string }> = {
  Low: { icon: ShieldCheck, color: "text-emerald-600", bg: "bg-emerald-500/10" },
  Medium: { icon: Shield, color: "text-amber-600", bg: "bg-amber-500/10" },
  High: { icon: ShieldAlert, color: "text-red-600", bg: "bg-red-500/10" },
};

type CardStatus = "open" | "monitoring" | "dismissed";

const RiskCardComponent = ({ risk }: { risk: FleetRisk }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState<CardStatus>("open");
  const DirectionIcon = directionIcons[risk.evidenceDirection];

  if (status === "dismissed") return null;

  return (
    <Card className={`border-l-4 ${severityStyles[risk.severity]} ${status === "monitoring" ? "opacity-60" : ""}`}>
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="h-4 w-4 text-muted-foreground shrink-0" />
            <h3 className="font-semibold text-foreground leading-tight">{risk.title}</h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline" className={severityBadge[risk.severity]}>
              {risk.severity.charAt(0).toUpperCase() + risk.severity.slice(1)}
            </Badge>
          </div>
        </div>

        {/* Evidence + meta */}
        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
          <div className="flex items-center gap-1">
            <DirectionIcon className="h-3.5 w-3.5" />
            <span>{risk.evidenceLabel}: <span className="font-medium text-foreground">{risk.evidenceValue}</span></span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            <span>{horizonLabels[risk.timeHorizon]}</span>
          </div>
          <span className="capitalize">Confidence: {risk.confidence}</span>
        </div>

        {/* Affected vehicles */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-muted-foreground">Affected:</span>
          {risk.affectedVehicles.slice(0, 3).map((v) => (
            <Badge key={v.id} variant="secondary" className="text-xs">
              {v.name}
            </Badge>
          ))}
          {risk.affectedVehicles.length > 3 && (
            <span className="text-xs text-muted-foreground">+{risk.affectedVehicles.length - 3} more</span>
          )}
        </div>

        {/* Why it matters */}
        <p className="text-sm text-muted-foreground mt-3">{risk.whyItMatters}</p>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
            {expanded ? "Less" : "Details"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8 text-muted-foreground"
            onClick={() => setStatus("monitoring")}
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            Mark as Monitoring
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8 text-muted-foreground"
            onClick={() => setStatus("dismissed")}
          >
            <EyeOff className="h-3.5 w-3.5 mr-1" />
            Dismiss for 30 days
          </Button>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-4 border-t pt-4 space-y-4">
            {/* Supporting metrics */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Supporting Metrics</p>
              <div className="grid gap-1.5">
                {risk.details.metrics.map((m) => (
                  <div key={m.label} className="text-sm">
                    <span className="text-muted-foreground">{m.label}: </span>
                    <span className="font-medium text-foreground">{m.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* What to monitor */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">What to Monitor</p>
              <ul className="space-y-1">
                {risk.details.whatToMonitor.map((item) => (
                  <li key={item} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Possible actions */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Possible Actions to Consider</p>
              <ul className="space-y-1">
                {risk.details.possibleActions.map((item) => (
                  <li key={item} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const WhatCouldGoWrong = () => {
  const { risks, riskSummary, vehicleData, isLoading } = useFleetRisks();
  const LevelIcon = riskLevelConfig[riskSummary.level].icon;

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-8 max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-foreground">What Could Go Wrong?</h1>
            <p className="text-muted-foreground mt-1">
              Structural risks that could impact profit or stability.
            </p>
          </div>

          {!isLoading && vehicleData.length > 0 && (
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg ${riskLevelConfig[riskSummary.level].bg}`}>
              <LevelIcon className={`h-5 w-5 ${riskLevelConfig[riskSummary.level].color}`} />
              <div className="text-right">
                <p className={`text-lg font-bold leading-none ${riskLevelConfig[riskSummary.level].color}`}>
                  {riskSummary.score}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {riskSummary.level} Risk
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
        ) : vehicleData.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold text-foreground">Need fleet data</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add vehicles and import trips and expenses to see structural risk analysis.
              </p>
            </CardContent>
          </Card>
        ) : risks.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <ShieldCheck className="h-10 w-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground">No structural risks detected</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Your fleet appears stable based on current trends.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {risks.map((risk) => (
              <RiskCardComponent key={risk.id} risk={risk} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default WhatCouldGoWrong;
